import type { CharacterAsset } from "./character-asset.js";
import { copyMatrix, multiplyMatrices, writeTrs } from "./character-pose.js";

export const RAGDOLL_FIXED_STEP = 1 / 60;
// Accept at most 0.5s per update, retaining 1/60 integration through severe
// frame delays. Excess time is discarded; this is not an FPS guarantee.
export const RAGDOLL_MAX_SUBSTEPS = 30;
export const RAGDOLL_CONSTRAINT_ITERATIONS = 6;
// Two articulated actors bound the saturated 30-step terrain workload; further
// actors retain the existing authored fallback under authoritative motion state.
export const RAGDOLL_MAX_ACTIVE_CHARACTERS = 2;

/** Decorative terrain in character model space. The server owns the root and solids. */
export interface RagdollGround { heightAt(x: number, z: number): number }
const FLAT_GROUND: RagdollGround = { heightAt: () => 0 };

// Generalized coordinates: fixed-length articulated segments, anatomical angular
// constraints, gravity/contact torques and a translating pelvis. No bone stretch
// can accumulate because every iteration reconstructs the original hierarchy.
export class ArticulatedRagdoll {
  readonly translations: Float32Array;
  readonly rotations: Float32Array;
  readonly globalMatrices: Float32Array;
  readonly angles: Float32Array;
  readonly angularVelocity: Float32Array;
  readonly activeBodies: number;
  readonly runtimeBytes: number;
  steps = 0;
  droppedSeconds = 0;
  age = 0;
  private accumulator = 0;
  private verticalVelocity = 0.4;
  private readonly localMatrices: Float32Array;
  private readonly scales: Float32Array;
  private readonly limits: Float32Array;
  private readonly radii: Float32Array;
  private readonly previousAngles: Float32Array;
  private readonly tips: readonly { joint: number; point: readonly [number, number, number]; radius: number }[];

  constructor(private readonly asset: CharacterAsset, translations: Float32Array, rotations: Float32Array,
    impulseX: number, impulseZ: number, variant: number) {
    const count = asset.joints.length;
    this.translations = new Float32Array(translations);
    this.rotations = new Float32Array(rotations);
    this.globalMatrices = new Float32Array(count * 16);
    this.localMatrices = new Float32Array(count * 16);
    this.angles = new Float32Array(count * 3);
    this.angularVelocity = new Float32Array(count * 3);
    this.previousAngles = new Float32Array(count * 3);
    this.scales = new Float32Array(count * 3).fill(1);
    this.limits = new Float32Array(count * 6);
    this.radii = new Float32Array(count);
    this.activeBodies = count - 1;
    this.tips = [
      { joint: 3, point: [0, 0.17, 0], radius: 0.10 },
      { joint: 5, point: [0, -0.40, 0.055], radius: 0.08 },
      { joint: 7, point: [0, -0.40, 0.055], radius: 0.08 },
      { joint: 9, point: [0.013, -0.40, 0.025], radius: 0.055 },
      { joint: 11, point: [-0.013, -0.40, 0.025], radius: 0.055 },
    ];
    for (let i = 0; i < count; i += 1) {
      const q = i * 4, a = i * 3, x = rotations[q]!, y = rotations[q + 1]!, z = rotations[q + 2]!, w = rotations[q + 3]!;
      this.angles[a] = Math.atan2(2 * (w * x - y * z), 1 - 2 * (x * x + y * y));
      this.angles[a + 1] = Math.asin(clamp(2 * (w * y + z * x), -1, 1));
      this.angles[a + 2] = Math.atan2(2 * (w * z - x * y), 1 - 2 * (y * y + z * z));
      const name = asset.joints[i]!.name;
      const limit = name === "pelvis" ? [-1.95, 1.95, -0.6, 0.6, -0.85, 0.85]
        : name.startsWith("lower_leg") ? [0.02, 2.35, 0, 0, 0, 0]
          : name.startsWith("lower_arm") ? [-2.4, -0.03, 0, 0, 0, 0]
            : name.startsWith("upper_leg") ? [-1.4, 1.1, -0.35, 0.35, -0.7, 0.7]
              : name.startsWith("upper_arm") ? [-2.6, 1.1, -0.65, 0.65, -1.3, 1.3]
                : name === "root" ? [0, 0, 0, 0, 0, 0] : [-0.55, 0.55, -0.6, 0.6, -0.4, 0.4];
      this.limits.set(limit, i * 6);
      this.radii[i] = name === "pelvis" ? 0.16 : name === "spine" ? 0.16 : name === "head" ? 0.11 : 0.065;
    }
    // The compact authoritative variant determines the initial tipping impulse;
    // subsequent articulation comes from gravity and terrain reaction.
    this.angularVelocity[3] = (variant === 1 ? -1 : 1) * (3.5 + Math.min(1, Math.abs(impulseZ) * 0.2));
    this.angularVelocity[5] = clamp(-impulseX * 0.3, -0.8, 0.8);
    for (const joint of [4, 6, 8, 10]) this.angularVelocity[joint * 3] = (joint % 4 ? -1 : 1) * 1.2;
    this.runtimeBytes = [this.translations, this.rotations, this.globalMatrices, this.localMatrices, this.angles,
      this.angularVelocity, this.previousAngles, this.scales, this.limits, this.radii].reduce((sum, array) => sum + array.byteLength, 0);
    this.updateMatrices();
  }

  update(delta: number, ground: RagdollGround = FLAT_GROUND): void {
    const elapsed = Number.isFinite(delta) ? Math.max(0, delta) : 0;
    const accepted = Math.min(elapsed, RAGDOLL_FIXED_STEP * RAGDOLL_MAX_SUBSTEPS);
    this.droppedSeconds += elapsed - accepted;
    this.accumulator += accepted;
    let substeps = 0;
    while (this.accumulator + 1e-9 >= RAGDOLL_FIXED_STEP && substeps < RAGDOLL_MAX_SUBSTEPS) {
      this.step(ground);
      this.accumulator = Math.max(0, this.accumulator - RAGDOLL_FIXED_STEP);
      substeps += 1;
    }
  }

  /** World root remains untouched. Radius-sized terrain proxies include extremities. */
  minimumGroundClearance(ground: RagdollGround = FLAT_GROUND): number {
    let clearance = Infinity;
    this.contacts((x, y, z, radius) => { clearance = Math.min(clearance, y - radius - ground.heightAt(x, z)); });
    return clearance;
  }

  get settled(): boolean {
    return this.age > 1 && this.angularVelocity.every((v) => Math.abs(v) < 0.35) && Math.abs(this.verticalVelocity) < 0.2;
  }

  /** Angle of the posed pelvis up axis from world up, independent of fall direction. */
  get pelvisTilt(): number {
    const x = this.globalMatrices[20]!, y = this.globalMatrices[21]!, z = this.globalMatrices[22]!;
    return Math.acos(clamp(y / (Math.hypot(x, y, z) || 1), -1, 1));
  }

  private step(ground: RagdollGround): void {
    const dt = RAGDOLL_FIXED_STEP;
    const previousPelvisY = this.translations[4]!;
    this.age += dt; this.steps += 1;
    this.previousAngles.set(this.angles);
    this.verticalVelocity = Math.max(-4, this.verticalVelocity - 9.81 * dt);
    this.translations[4] = this.translations[4]! + this.verticalVelocity * dt;
    // Gravity is transformed into each joint's parent frame. Its moment arm is
    // the actual posed segment, so a limb's response changes when the torso rolls.
    for (let joint = 1; joint < this.asset.joints.length; joint += 1) {
      const offset = joint * 16, parent = this.asset.joints[joint]!.parent * 16;
      const upward = joint < 4 ? 1 : -1;
      const rz = this.globalMatrices[offset + 6]! * upward;
      const rx = this.globalMatrices[offset + 4]! * upward;
      const torqueX = rz * 13, torqueZ = -rx * 13;
      const a = joint * 3;
      const damping = Math.exp(-(this.age > 1.2 ? 6 : 2.2) * dt);
      this.angularVelocity[a] = clamp((this.angularVelocity[a]! + (torqueX * this.globalMatrices[parent]! + torqueZ * this.globalMatrices[parent + 2]!) * dt) * damping, -6, 6);
      this.angularVelocity[a + 2] = clamp((this.angularVelocity[a + 2]! + (torqueX * this.globalMatrices[parent + 8]! + torqueZ * this.globalMatrices[parent + 10]!) * dt) * damping, -6, 6);
      for (let axis = 0; axis < 3; axis += 1) {
        const index = a + axis, minimum = this.limits[joint * 6 + axis * 2]!, maximum = this.limits[joint * 6 + axis * 2 + 1]!;
        const next = this.angles[index]! + this.angularVelocity[index]! * dt;
        this.angles[index] = clamp(next, minimum, maximum);
        if (next < minimum || next > maximum) this.angularVelocity[index] = 0;
      }
    }
    let contacted = false;
    for (let iteration = 0; iteration < RAGDOLL_CONSTRAINT_ITERATIONS; iteration += 1) {
      this.updateMatrices();
      let penetration = 0, px = 0, pz = 0, contactJoint = 0;
      this.contacts((x, y, z, radius, joint) => {
        const depth = ground.heightAt(x, z) + radius - y;
        if (depth > penetration) { penetration = depth; px = x; pz = z; contactJoint = joint; }
      });
      if (penetration <= 1e-5) break;
      contacted = true;
      // Angular position-based contact: project a sphere above terrain using
      // the joint-chain Jacobian and pelvis translation together. Rotational
      // response therefore moves the contacted limb instead of lifting the
      // entire torso onto a rigid outstretched arm.
      let denominator = 0.12;
      for (let joint = contactJoint; joint > 0; joint = this.asset.joints[joint]!.parent) {
        const m = joint * 16, parent = this.asset.joints[joint]!.parent * 16;
        const rx = px - this.globalMatrices[m + 12]!, rz = pz - this.globalMatrices[m + 14]!;
        for (const axis of [0, 2]) {
          const gradient = this.globalMatrices[parent + axis * 4 + 2]! * rx - this.globalMatrices[parent + axis * 4]! * rz;
          denominator += gradient * gradient * 12;
        }
      }
      const correction = penetration / denominator;
      this.translations[4] = this.translations[4]! + correction * 0.12;
      for (let joint = contactJoint; joint > 0; joint = this.asset.joints[joint]!.parent) {
        const m = joint * 16, parent = this.asset.joints[joint]!.parent * 16;
        const rx = px - this.globalMatrices[m + 12]!, rz = pz - this.globalMatrices[m + 14]!;
        for (const axis of [0, 2]) {
          const gradient = this.globalMatrices[parent + axis * 4 + 2]! * rx - this.globalMatrices[parent + axis * 4]! * rz;
          const index = joint * 3 + axis;
          this.angles[index] = clamp(this.angles[index]! + gradient * correction * 12,
            this.limits[joint * 6 + axis * 2]!, this.limits[joint * 6 + axis * 2 + 1]!);
        }
      }
    }
    this.updateMatrices();
    const clearance = this.minimumGroundClearance(ground);
    if (clearance < 0) this.translations[4] = this.translations[4]! - clearance;
    // A folding contacted limb can still let the torso descend. Reconstruct its
    // projected velocity instead of cancelling gravity on every limb contact;
    // upward penetration corrections must not become rebound energy.
    if (contacted) this.verticalVelocity = clamp((this.translations[4]! - previousPelvisY) / dt, -4, 0);
    for (let i = 3; i < this.angularVelocity.length; i += 1) {
      this.angularVelocity[i] = clamp((this.angles[i]! - this.previousAngles[i]!) / dt, -6, 6) * (contacted ? 0.88 : 1);
    }
    // Presentation can neither drift across the world nor escape the root.
    this.translations[3] = clamp(this.translations[3]!, -0.35, 0.35);
    this.translations[5] = clamp(this.translations[5]!, -0.35, 0.35);
    this.updateMatrices();
  }

  private contacts(visit: (x: number, y: number, z: number, radius: number, joint: number) => void): void {
    for (let joint = 1; joint < this.asset.joints.length; joint += 1) {
      const m = joint * 16;
      visit(this.globalMatrices[m + 12]!, this.globalMatrices[m + 13]!, this.globalMatrices[m + 14]!, this.radii[joint]!, joint);
      const parent = this.asset.joints[joint]!.parent;
      if (parent > 0) {
        const p = parent * 16;
        visit((this.globalMatrices[m + 12]! + this.globalMatrices[p + 12]!) / 2,
          (this.globalMatrices[m + 13]! + this.globalMatrices[p + 13]!) / 2,
          (this.globalMatrices[m + 14]! + this.globalMatrices[p + 14]!) / 2, this.radii[joint]!, joint);
      }
    }
    for (const tip of this.tips) {
      const m = tip.joint * 16, [x, y, z] = tip.point;
      visit(this.globalMatrices[m]! * x + this.globalMatrices[m + 4]! * y + this.globalMatrices[m + 8]! * z + this.globalMatrices[m + 12]!,
        this.globalMatrices[m + 1]! * x + this.globalMatrices[m + 5]! * y + this.globalMatrices[m + 9]! * z + this.globalMatrices[m + 13]!,
        this.globalMatrices[m + 2]! * x + this.globalMatrices[m + 6]! * y + this.globalMatrices[m + 10]! * z + this.globalMatrices[m + 14]!, tip.radius, tip.joint);
    }
  }

  private updateMatrices(): void {
    for (let joint = 0; joint < this.asset.joints.length; joint += 1) {
      const a = joint * 3, q = joint * 4, m = joint * 16;
      const x = this.angles[a]! / 2, y = this.angles[a + 1]! / 2, z = this.angles[a + 2]! / 2;
      const sx = Math.sin(x), cx = Math.cos(x), sy = Math.sin(y), cy = Math.cos(y), sz = Math.sin(z), cz = Math.cos(z);
      this.rotations[q] = sx * cy * cz + cx * sy * sz;
      this.rotations[q + 1] = cx * sy * cz - sx * cy * sz;
      this.rotations[q + 2] = cx * cy * sz + sx * sy * cz;
      this.rotations[q + 3] = cx * cy * cz - sx * sy * sz;
      writeTrs(this.localMatrices, m, this.translations, a, this.rotations, q, this.scales, a);
      const parent = this.asset.joints[joint]!.parent;
      if (parent < 0) copyMatrix(this.globalMatrices, m, this.localMatrices, m);
      else multiplyMatrices(this.globalMatrices, m, this.globalMatrices, parent * 16, this.localMatrices, m);
    }
  }
}

function clamp(value: number, minimum: number, maximum: number): number { return Math.max(minimum, Math.min(maximum, value)); }
